<script>
import { useEsignStore } from '../../stores/esign'
import { extractDeepErrorMessage } from '../../composables/useApiErrors'
import SignatureTable from './SignatureTable.vue'
import SignatureForm from './SignatureForm.vue'
import SignatureDetail from './SignatureDetail.vue'
import EcDrawer from '../ui/EcDrawer.vue'
import EcButton from '../ui/EcButton.vue'

export default {
  name: 'SignatureList',
  components: {
    SignatureTable,
    SignatureForm,
    SignatureDetail,
    EcDrawer,
    EcButton,
  },
  props: {
    signableType: {
      type: String,
      required: true,
    },
    signableId: {
      type: [String, Number],
      required: true,
    },
    clickableRows: {
      type: Boolean,
      default: false,
    },
    actionsMode: {
      type: String,
      default: 'inline',
    },
  },
  data() {
    const store = useEsignStore()
    return {
      store,
      refreshKey: 0,
      showCreateDrawer: false,
      showEditDrawer: false,
      showViewDrawer: false,
      viewDrawerFullscreen: false,
      selectedSignature: null,
      formSubmitting: false,
      formError: null,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate
    },
  },
  methods: {
    onView(signature) {
      this.selectedSignature = signature
      this.viewDrawerFullscreen = false
      this.showViewDrawer = true
    },
    onViewRequestFullscreen() {
      this.viewDrawerFullscreen = true
    },
    onEdit(signature) {
      this.selectedSignature = signature
      this.formError = null
      this.showEditDrawer = true
    },
    async onDelete(signature) {
      const confirmed = this.$ecConfirm
        ? await this.$ecConfirm(`Êtes-vous sûr de vouloir supprimer "${signature.name}" ?`)
        : window.confirm(`Êtes-vous sûr de vouloir supprimer "${signature.name}" ?`)

      if (!confirmed) return

      try {
        await this.store.deleteSignature(this.signableType, this.signableId, signature.id)
        this.$ecNotify(this.$ecTranslate('ELIXIO_CONNECT.SIGNATURE_DELETED'), 'success')
        this.refreshKey++
      } catch (err) {
        this.$ecNotify(this.extractErrorMessage(err), 'error')
      }
    },
    async onSend(signature) {
      const confirmed = this.$ecConfirm
        ? await this.$ecConfirm(`Envoyer "${signature.name}" pour signature ?`)
        : window.confirm(`Envoyer "${signature.name}" pour signature ?`)

      if (!confirmed) return

      try {
        await this.store.sendSignature(this.signableType, this.signableId, signature.id)
        this.$ecNotify(this.$ecTranslate('ELIXIO_CONNECT.SIGNATURE_SENT'), 'success')
        this.refreshKey++
      } catch (err) {
        this.$ecNotify(this.extractErrorMessage(err), 'error')
      }
    },
    onCreateNew() {
      this.selectedSignature = null
      this.formError = null
      this.showCreateDrawer = true
    },
    extractErrorMessage(err) {
      return extractDeepErrorMessage(err, this.$ecTranslate('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'))
    },
    async onFormSubmitted(formData) {
      this.formSubmitting = true
      this.formError = null

      try {
        // Build payload matching the API expected format
        var payload = {
          name: formData.name,
          description: formData.description,
          email_message: formData.email_message,
          meta: formData.meta,
          signers: formData.signers.map(function (s) {
            return {
              role: s.role,
              email: s.email,
              firstname: s.first_name,
              lastname: s.last_name,
              address: s.address,
            }
          }),
        }

        if (formData.template_id) {
          payload.model_id = formData.template_id
        }
        if (formData.model_code) {
          payload.model_code = formData.model_code
        }
        if (formData.send_immediately) {
          payload.sendNow = 1
        }

        // Fields payload: use raw template fields (nested OneSpan format) when available,
        // otherwise fall back to flat format
        if (formData.rawTemplateFields && formData.rawTemplateFields.length) {
          payload.fields = formData.rawTemplateFields
        } else if (formData.fields && formData.fields.length) {
          payload.fields = formData.fields.map(function (f) {
            var entry = {
              code: f.code,
              value: f.value,
            }
            if (f.document_id) {
              entry.document_id = f.document_id
            }
            return entry
          })
        }

        // Document replacements
        var documentsToReplace = (formData.documents || [])
          .filter(function (doc) { return doc.replacementBase64 })
          .map(function (doc) {
            return {
              target_name: doc.originalName,
              base64: doc.replacementBase64,
              name: doc.replacementFileName || doc.originalName,
            }
          })
        if (documentsToReplace.length) {
          payload.documents = documentsToReplace
        }

        if (this.selectedSignature) {
          // Edit mode
          await this.store.updateSignature(
            this.signableType,
            this.signableId,
            this.selectedSignature.id,
            payload,
          )
          this.$ecNotify(this.$ecTranslate('ELIXIO_CONNECT.SIGNATURE_UPDATED'), 'success')
        } else {
          // Create mode
          var created = await this.store.createSignature(this.signableType, this.signableId, payload)
          this.$ecNotify(this.$ecTranslate('ELIXIO_CONNECT.SIGNATURE_ADDED'), 'success')

          // Open detail view for the newly created signature
          if (created && created.id) {
            this.showCreateDrawer = false
            this.refreshKey++
            this.selectedSignature = created
            this.viewDrawerFullscreen = false
            this.showViewDrawer = true
            return
          }
        }

        this.showCreateDrawer = false
        this.showEditDrawer = false
        this.refreshKey++
      } catch (err) {
        this.formError = this.extractErrorMessage(err)
        this.$ecNotify(this.formError, 'error')
      } finally {
        this.formSubmitting = false
      }
    },
  },
}
</script>

<template>
  <div class="ec-signature-list">
    <!-- Title bar -->
    <div class="ec-flex ec-justify-between ec-items-center ec-mb-3">
      <h5 style="margin: 0">{{ t('ELIXIO_CONNECT.SIGNATURES') }}</h5>
      <EcButton variant="primary" size="sm" icon="plus-lg" @click="onCreateNew">
        {{ t('ELIXIO_CONNECT.ADD_SIGNATURE') }}
      </EcButton>
    </div>

    <!-- Signature table -->
    <SignatureTable
      :signable-type="signableType"
      :signable-id="signableId"
      :refresh-key="refreshKey"
      :clickable-rows="clickableRows"
      :actions-mode="actionsMode"
      @view="onView"
      @edit="onEdit"
      @delete="onDelete"
      @send="onSend"
    />

    <!-- Create drawer -->
    <EcDrawer :value="showCreateDrawer" :title="t('ELIXIO_CONNECT.ADD_SIGNATURE')" width="1000px" resizable @input="showCreateDrawer = $event">
      <SignatureForm
        :signable-type="signableType"
        :signable-id="signableId"
        :submitting="formSubmitting"
        :submit-error="formError"
        @submitted="onFormSubmitted"
        @cancel="showCreateDrawer = false"
      />
    </EcDrawer>

    <!-- Edit drawer -->
    <EcDrawer :value="showEditDrawer" :title="t('ELIXIO_CONNECT.EDIT_SIGNATURE')" width="1000px" resizable @input="showEditDrawer = $event">
      <SignatureForm
        v-if="selectedSignature"
        :signable-type="signableType"
        :signable-id="signableId"
        :signature="selectedSignature"
        :submitting="formSubmitting"
        :submit-error="formError"
        @submitted="onFormSubmitted"
        @cancel="showEditDrawer = false"
      />
    </EcDrawer>

    <!-- View drawer -->
    <EcDrawer :value="showViewDrawer" :title="t('ELIXIO_CONNECT.VIEW_SIGNATURE')" width="1000px" resizable :fullscreen="viewDrawerFullscreen" @input="showViewDrawer = $event">
      <SignatureDetail
        v-if="selectedSignature"
        :signable-type="signableType"
        :signable-id="signableId"
        :signature-id="selectedSignature.id"
        @edit="showViewDrawer = false; onEdit(selectedSignature)"
        @delete="showViewDrawer = false; refreshKey++"
        @send="refreshKey++"
        @close="showViewDrawer = false"
        @request-fullscreen="onViewRequestFullscreen"
      />
    </EcDrawer>
  </div>
</template>
